import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Dialog, Dropdown, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Delete, Link, Pencil, Plus, Reset } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { WorkingConditionsModal } from 'modules/job_catalog/components/WorkingConditionsModal'
import useCreateJobCatalogAttribute from 'modules/job_catalog/hooks/useCreateJobCatalogAttribute'
import useDeleteJobCatalogAttribute from 'modules/job_catalog/hooks/useDeleteJobCatalogAttribute'
import useEditJobCatalogAttribute from 'modules/job_catalog/hooks/useEditJobCatalogAttribute'
import { useTreeNode } from 'modules/job_catalog/hooks/useTreeNode'
import { getNavigationForTypeFromUuid, normalizeNodeType } from 'modules/job_catalog/lib/nodesUtils'
import {
  WorkingConditions,
  getTranslationTextValue,
} from 'modules/job_catalog/lib/workingConditions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const workingConditionStyling = { flex: '0 0 30%' }

const WorkingConditionsPage = () => {
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const nodeUuid = levelId || roleId || functionId || familyId || rootId || ''

  const [showWorkingConditionsModal, setShowWorkingConditionsModal] = useState(false)
  const [showDeleteWorkingConditionsDialog, setShowDeleteWorkingConditionsDialog] = useState(false)
  const [isResetDialog, setIsResetDialog] = useState(false)

  const { company } = useCurrentSession()
  const {
    nodeName,
    currentNode,
    currentAttributes,
    workingConditions: currentWorkingConditions,
    workingConditionsFromParentNode,
  } = useTreeNode(nodeUuid)

  const workingConditionAttribute = currentAttributes.find(
    (attribute) => attribute.type === 'working_conditions'
  )
  const { createWorkingConditions } = useCreateJobCatalogAttribute()
  const { updateWorkingConditions } = useEditJobCatalogAttribute()
  const { deleteAttribute } = useDeleteJobCatalogAttribute()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const saveWorkingConditions = async (workingConditions: WorkingConditions) => {
    if (workingConditions.id) {
      await handleUpdateWorkingConditions(workingConditions)
    } else {
      await handleCreateWorkingConditions(workingConditions)
    }
    setShowWorkingConditionsModal(false)
  }

  const handleCreateWorkingConditions = async (workingConditions: WorkingConditions) => {
    const result = await createWorkingConditions(company.id, nodeUuid, workingConditions)
    const handled = handleResult(result, (resultErrors) => {
      app.setFlash({
        type: 'wrong',
        message: resultErrors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.working_conditions.modal.success'),
        time: 5000,
      })
    }
  }

  const handleUpdateWorkingConditions = async (workingConditions: WorkingConditions) => {
    if (!workingConditions.id) return
    const result = await updateWorkingConditions(
      workingConditions.id,
      company.id,
      nodeUuid,
      workingConditions
    )
    const handled = handleResult(result, (resultErrors) => {
      app.setFlash({
        type: 'wrong',
        message: resultErrors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.working_conditions.modal.success'),
        time: 5000,
      })
    }
  }

  const deleteWorkingConditions = async () => {
    if (!currentWorkingConditions?.id) return
    const result = await deleteAttribute(currentWorkingConditions.id, nodeUuid)

    const handled = handleResult(result, (resultErrors) => {
      app.setFlash({
        type: 'wrong',
        message: resultErrors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.working_conditions.dialog.success'),
        time: 5000,
      })
    }
    setShowDeleteWorkingConditionsDialog(false)
  }

  const handleNavigateToInheritedWorkingConditionsNode = () => {
    const navigationForInheritedWorkingConditionsNode = getNavigationForTypeFromUuid(
      navigation,
      workingConditionAttribute?.definedAtNode.uuid || ''
    )

    navigate(
      navigationForInheritedWorkingConditionsNode.workingConditions.fullPath({
        rootId: rootId || '',
        familyId: familyId || '',
        functionId: functionId || '',
        roleId: roleId || '',
        levelId: levelId || '',
      })
    )
  }

  return (
    <ResponsivePage.Body variant="full">
      {currentWorkingConditions ? (
        <>
          <Box
            paddingBottom="s20"
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="flexStart"
          >
            <Box gap="s8" alignItems="flexStart">
              <Text size="300" weight="semibold" color="textPrimary">
                {currentWorkingConditions?.name}
              </Text>
              <Box maxWidth="s600">
                <Text size="200" color="neutralForegroundSecondary">
                  {currentWorkingConditions?.description}
                </Text>
              </Box>
              {currentWorkingConditions?.inherited && workingConditionAttribute && (
                <F0Button
                  variant="neutral"
                  label={workingConditionAttribute.definedAtNode.name || i18n.t('common.company')}
                  icon={Link}
                  size="sm"
                  onClick={handleNavigateToInheritedWorkingConditionsNode}
                />
              )}
            </Box>
            <Dropdown
              items={
                currentWorkingConditions.inherited
                  ? [
                      {
                        label: i18n.t(
                          'job_catalog.nodes.working_conditions.new_from_inherited.title'
                        ),
                        description: i18n.t(
                          'job_catalog.nodes.working_conditions.new_from_inherited.description',
                          { node: normalizeNodeType(currentNode?.type) }
                        ),
                        icon: Plus,
                        onClick: () => setShowWorkingConditionsModal(true),
                      },
                    ]
                  : [
                      {
                        label: i18n.t('job_catalog.nodes.working_conditions.edit'),
                        icon: Pencil,
                        onClick: () => setShowWorkingConditionsModal(true),
                      },
                      workingConditionsFromParentNode
                        ? {
                            label: i18n.t('job_catalog.nodes.working_conditions.restore'),
                            description: workingConditionsFromParentNode.name,
                            icon: Reset,
                            onClick: () => {
                              setIsResetDialog(true)
                              setShowDeleteWorkingConditionsDialog(true)
                            },
                          }
                        : {
                            label: i18n.t('job_catalog.nodes.working_conditions.delete'),
                            icon: Delete,
                            onClick: () => {
                              setIsResetDialog(false)
                              setShowDeleteWorkingConditionsDialog(true)
                            },
                            critical: true,
                          },
                    ]
              }
            />
          </Box>
          <Box
            paddingY="s20"
            gap="s20"
            flexDirection="row"
            flexWrap="wrap"
            justifyContent="flexStart"
            border={{
              top: {
                width: 's1',
                style: 'dashed',
                color: 'grey400',
              },
              bottom: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            {currentWorkingConditions?.conditions.map((condition) => {
              const match = getTranslationTextValue(condition)
              if (!match) return null
              const [text, value] = match
              return (
                <Box key={condition.key} style={workingConditionStyling}>
                  <Text size="200">{text}</Text>
                  <Text size="200" weight="semibold">
                    {value}
                  </Text>
                </Box>
              )
            })}
            <Box style={workingConditionStyling}>
              <Text size="200">{i18n.t('job_catalog.nodes.working_conditions.applies_to')}</Text>
              <Text size="200" weight="semibold">
                {i18n.t('job_catalog.nodes.working_conditions.all_workplaces')}
              </Text>
            </Box>
          </Box>
        </>
      ) : (
        <Box flexGrow alignItems="center" width="full" minHeight="full" paddingTop="s80">
          <OneEmptyState
            actions={[
              {
                label: i18n.t('job_catalog.nodes.empty_state.working_conditions.actions.new'),
                icon: Plus,
                onClick: () => setShowWorkingConditionsModal(true),
                variant: 'outline',
              },
            ]}
            description={i18n.t('job_catalog.nodes.empty_state.working_conditions.description')}
            emoji="📄"
            title={i18n.t('job_catalog.nodes.empty_state.working_conditions.title')}
          />
        </Box>
      )}
      {showWorkingConditionsModal && (
        <WorkingConditionsModal
          nodeName={nodeName ?? ''}
          initialWorkingConditions={currentWorkingConditions}
          onClose={() => setShowWorkingConditionsModal(false)}
          onSubmit={saveWorkingConditions}
        />
      )}
      <Dialog
        actions={{
          primary: {
            label: isResetDialog
              ? i18n.t('job_catalog.nodes.working_conditions.dialog.reset')
              : i18n.t('job_catalog.nodes.working_conditions.dialog.delete'),
            onClick: deleteWorkingConditions,
            variant: 'critical',
          },
          secondary: {
            label: i18n.t('job_catalog.nodes.working_conditions.dialog.cancel'),
            onClick: () => setShowDeleteWorkingConditionsDialog(false),
          },
        }}
        header={{
          description: isResetDialog
            ? i18n.t('job_catalog.nodes.working_conditions.dialog.descriptionResetFull', {
                nodeName: currentNode?.name,
                workingConditionsName: currentWorkingConditions?.name,
                inheritedNodeName: currentWorkingConditions?.inherited
                  ? workingConditionAttribute?.definedAtNode.name
                  : i18n.t('common.company'),
                workingConditionsParentName: workingConditionsFromParentNode?.name,
              })
            : i18n.t('job_catalog.nodes.working_conditions.dialog.description'),
          title: isResetDialog
            ? i18n.t('job_catalog.nodes.working_conditions.dialog.titleReset')
            : i18n.t('job_catalog.nodes.working_conditions.dialog.title', {
                workingConditionsName: currentWorkingConditions?.name,
              }),
          type: 'critical',
        }}
        open={showDeleteWorkingConditionsDialog}
        onClose={() => setShowDeleteWorkingConditionsDialog(false)}
      />
    </ResponsivePage.Body>
  )
}

export default WorkingConditionsPage
